import {
  addGameObject,
  anchoredPos,
  moveEntityToBack,
  getZeroEntity,
  getZeroStaticEntity,
  removeGameObjectByEntity,
  setParent,
  unParentEntity,
  getObjectsByType,
  getObjectCountByType,
} from './entity';
import { createAndAddLocalContainer } from './container';
import { t } from './localizationController';
import { isAppearing, lerpNumber, removeDenseArrayIndex, v2 } from './helpers';
import {
  ObjectType,
  RoomNavigationType,
  RoomUserInfo,
  ServerEvent_Room,
} from './shared/api_shared_types';
import { setVisibilityState } from './simulate';
import {
  Canvas,
  Container,
  VisibilityState,
  GameManager,
  GameObject,
  MoveMode,
  RenderItemType,
  Room,
  RoomId,
  RoomUser,
  RoomUserEntity,
  Shape,
  SharedObjectType,
  Size,
  UserRoomId,
  type GameObjectController,
  type V2,
} from './types';
import { pgPath, debugState, convertUrl } from './index';

import { createAndAddLocalCanvas } from './canvas';

import { SHAPE_STYLES } from './constants';

import * as IntercomApi from '@intercom/messenger-js-sdk';
import logger from './controllers/logger';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
} from './render_package';
import * as Background from './background_controller';

export function controller(): GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    createPackage,
    dispose,
  };
}

export function initRoom(manager: GameManager, name: string, roomId: UserRoomId): Room {
  let newRoom: Room = {
    ...getZeroEntity(),
    objectType: SharedObjectType.ROOM,
    name,
    message: '',
    closeRoomLabel: '',
    joined: false,
    useNewVideocallUI: false,
    users: [],
    syncCursorText: false,
    syncUserText: false,
    syncCursor: false,
    showCursor: false,
    subTitle: '',
    roomId,
    navigationType: RoomNavigationType.NONE,
    attachChild,
    canvas: null,

    content: null,
    lobbyLayer: null,
    instructionLayer: null,
    topUILayer: null,

    userIsHost: false,
    showCanvas: true,
  };

  newRoom.shape = Shape.RECT;
  newRoom.pivotPoint = v2();
  // newRoom.childAnchorPivot = v2();
  newRoom.parentRoomId = roomId;
  newRoom.allowDrag = false;
  newRoom.allowHighlight = false;
  newRoom.width = manager.space.width;
  newRoom.height = manager.space.height;
  newRoom.relativeWidth = 1;
  newRoom.relativeHeight = 1;

  addGameObject(manager, newRoom);

  manager.space.addRoom(newRoom, manager.space);

  {
    newRoom.content = createAndAddLocalContainer(manager, getZeroStaticEntity());
    newRoom.content.pivotPoint = v2();
    newRoom.content.childAnchorPivot = v2();
    setParent(newRoom.content, newRoom, v2());
    newRoom.content.width = newRoom.width;
    newRoom.content.height = newRoom.height;
    newRoom.content.relativeWidth = 1;
    newRoom.content.relativeHeight = 1;
    newRoom.content.height = newRoom.height;
    newRoom.content.fixedZIndex = true;
  }

  //create room-canvas
  {
    const c = createAndAddLocalCanvas(manager, getZeroStaticEntity(), {
      allowScrollVert: true,
      allowScrollHor: true,
      allowZoom: true,
      autoSize: true,
      linked: Background.getContainer(),
      debugView: false,
    });

    // c.layer.shapeStyle = SHAPE_STYLES.DEBUG;
    c.moveMode = MoveMode.ANCHORED;
    c.anchoredPos = anchoredPos(v2(0, 0), v2(0, 0), v2(0, 0));

    c.relativeWidth = 1;
    c.relativeHeight = 1;

    c.padWidth = 0;
    c.padHeight = 0;

    setParent(c, newRoom.content);

    c.onScaleChange = updateScaleUI;

    newRoom.canvas = c;

    moveEntityToBack(c);
    c.fixedZIndex = true;

    updateRoomCanvas(c);
  }

  {
    newRoom.instructionLayer = createAndAddLocalContainer(manager, getZeroStaticEntity());
    newRoom.instructionLayer.pivotPoint = v2();
    newRoom.instructionLayer.childAnchorPivot = v2(0, 0);
    setParent(newRoom.instructionLayer, newRoom, v2());
    newRoom.instructionLayer.relativeWidth = 1;
    newRoom.instructionLayer.relativeHeight = 1;
    newRoom.instructionLayer.height = newRoom.height;
    newRoom.instructionLayer.detachChild = detachFrontLayerChild;
    newRoom.instructionLayer.fixedZIndex = true;
  }

  {
    newRoom.lobbyLayer = createAndAddLocalContainer(manager, getZeroStaticEntity());
    newRoom.lobbyLayer.pivotPoint = v2();
    newRoom.lobbyLayer.childAnchorPivot = v2();
    setParent(newRoom.lobbyLayer, newRoom, v2());
    newRoom.lobbyLayer.relativeWidth = 1;
    newRoom.lobbyLayer.relativeHeight = 1;
    newRoom.lobbyLayer.height = newRoom.height;
    newRoom.lobbyLayer.detachChild = detachFrontLayerChild;
    newRoom.lobbyLayer.fixedZIndex = true;
  }

  {
    newRoom.topUILayer = createAndAddLocalContainer(manager, getZeroStaticEntity());
    newRoom.topUILayer.pivotPoint = v2();
    newRoom.topUILayer.childAnchorPivot = v2();
    setParent(newRoom.topUILayer, newRoom, v2());
    newRoom.topUILayer.relativeWidth = 1;
    newRoom.topUILayer.relativeHeight = 1;
    newRoom.topUILayer.height = newRoom.height;
    newRoom.topUILayer.detachChild = detachFrontLayerChild;
    newRoom.topUILayer.fixedZIndex = true;
  }

  if (newRoom.name) {
    document.title = newRoom.manager.pageTitle + ' - ' + newRoom.name;
  } else {
    document.title = newRoom.manager.pageTitle;
  }

  return newRoom;
}

function updateScaleUI(_canvas: Canvas, _scale: number) {}

function attachChild(child: GameObject, parent: Room) {
  switch (child.objectType) {
    case ObjectType.CMS_CONSOLE:
      {
        setParent(child, parent.lobbyLayer);
      }
      break;

    default:
      setParent(child, parent.content);
  }
}

function detachFrontLayerChild(child: GameObject, _container: Container) {
  unParentEntity(child);
}

function hideRoomContent(room: Room) {
  setVisibilityState(room.content, VisibilityState.HIDDEN);
  setVisibilityState(room.instructionLayer, VisibilityState.HIDDEN);
}

function showRoomContent(room: Room) {
  setVisibilityState(room.content, VisibilityState.DEFAULT);
  setVisibilityState(room.instructionLayer, VisibilityState.DEFAULT);
}

export function findRoomById(manager: GameManager, roomId: RoomId): Room | null {
  for (const room of getObjectsByType<Room>(manager, ObjectType.ROOM)) {
    if (room.roomId === roomId) {
      return room;
    }
  }
  return null;
}

export function syncRoom(manager: GameManager, data: ServerEvent_Room) {
  const roomId = data.roomId;

  let room = findRoomById(manager, roomId);

  if (!room) {
    room = initRoom(manager, data.name, roomId);
    room.serverObjId = data.sId;
    setVisibilityState(room, VisibilityState.DEFAULT);

    room.width = manager.space.width;
    room.height = manager.space.height;

    room.localZIndex = getObjectCountByType(manager, ObjectType.ROOM);

    if (manager.mainRoom) {
      history.replaceState(null, '', pgPath);
      terminateRoom(manager.mainRoom);
    }
    document.title = room.manager.pageTitle + ' - ' + room.name;
    manager.mainRoom = room;
    moveEntityToBack(room);
    room.fixedZIndex = true;
  }

  if ('syncUserText' in data) {
    room.syncUserText = data.syncUserText;
  }

  if ('syncCursorText' in data) {
    room.syncCursorText = data.syncCursorText;
  }

  if ('syncCursor' in data) {
    room.syncCursor = data.syncCursor;
  }
  if ('showCursor' in data) {
    room.showCursor = data.showCursor;
  }

  if ('userIsHost' in data) {
    room.userIsHost = data.userIsHost;
  }

  let updateUsersInToom = 'userInfo' in data || 'userCount' in data;
  if (updateUsersInToom) {
    fullUpdateUsersInRoom(manager, room, data.userInfo, data.roomUserCount);
  }

  if ('message' in data) {
    if (room.message !== data.message) {
      room.message = data.message;
    }
  }

  if ('name' in data) {
    updateRoomName(room, data.name);

    if (data.name && room.name !== data.name) {
      IntercomApi.trackEvent({ enterRoom: data.name });
    }
  }

  if ('navigationType' in data) {
    updateNavigationType(room, data.navigationType);
  }

  if ('joined' in data) {
    if (data.joined !== room.joined) {
      setJoined(room, data.joined);
    }
  }
}

function getOtherRoomUsersString(room: Room, userSlug: string): string {
  let result = '';

  const otherUsers = room.users.filter((it) => it.userSlug !== userSlug);

  if (otherUsers.length === 0) {
    result = '';
  } else if (otherUsers.length > 0 && otherUsers.length <= 2) {
    const otherUserNames = otherUsers.map((it) => it.firstName);
    const otherUserNamesText = otherUserNames.join(' & ');
    result = `${otherUserNamesText}`;
  } else if (otherUsers.length > 2) {
    result = `${otherUsers.length + 1} participants`;
  }

  return result;
}

function updateNavigationType(room: Room, navigationType: RoomNavigationType) {
  if (room.navigationType !== navigationType) {
    room.navigationType = navigationType;
  }
}
//TODO: feels ike duplicate of fullUpdateUsersInRoom function
export function updateUsersInRoom(manager: GameManager, json: ServerEvent_Room) {
  const room = findRoomById(manager, json.roomId);

  if (!room) return;

  const userInfo = json.userInfo;

  for (let i = userInfo.length - 1; i >= 0; i--) {
    const userUpdateData = userInfo[i];

    const existsIndex = room.users.findIndex((it) => it.slug === userUpdateData.slug);

    if (userUpdateData.isNew) {
      if (existsIndex < 0) {
        //add user
        const newRoomUser = createNewRoomUser(userUpdateData);

        if (userUpdateData.slug !== manager.user.slug) {
          const roomUserEntity = createAndAddRoomUserEntity(
            manager,
            newRoomUser.userSlug,
            newRoomUser.firstName,
            room.showCursor,
          );
          newRoomUser.roomUserEntity = roomUserEntity;

          if (room.canvas) {
            room.canvas.attachChild(roomUserEntity, room.canvas);
          } else {
            setParent(roomUserEntity, room);
          }
        }

        room.users.push(newRoomUser);
      }
    } else if (existsIndex >= 0) {
      const existingUser = room.users[existsIndex];
      if (userUpdateData.justLeft && !userUpdateData.isReservedUser) {
        //remove user
        setVisibilityState(existingUser.roomUserEntity, VisibilityState.DISAPPEARING);
        removeDenseArrayIndex(room.users, existsIndex);
      } else if (userUpdateData.justLeft && userUpdateData.isReservedUser) {
      } else {
        existingUser.firstName = userUpdateData.firstname;
        existingUser.lastName = userUpdateData.lastname;
        existingUser.profileUriThumb = userUpdateData.profileUriThumb;
        existingUser.profileUriMedium = userUpdateData.profileUriMedium;
        existingUser.disconnected = userUpdateData.disconnected;
      }
    }
  }

  //we do not want this icon anymore
  // updateRoomUserIcons(room.users, json.roomUserCount, manager.overlay);
}

//TODO: feels ike duplicate of updateUsersInRoom function
export function fullUpdateUsersInRoom(
  manager: GameManager,
  room: Room,
  userInfo: RoomUserInfo[],
  userCount: number,
) {
  //add roomUser we don't have yet
  if (userInfo) {
    for (let i = userInfo.length - 1; i >= 0; i--) {
      const userUpdateData = userInfo[i];

      if (userUpdateData.justLeft && !userUpdateData.isReservedUser) {
        const existsIndex = room.users.findIndex((it) => it.slug === userUpdateData.slug);
        if (existsIndex >= 0) {
          removeDenseArrayIndex(room.users, existsIndex);
        }
      } else {
        const existsIndex = room.users.findIndex((it) => it.slug === userUpdateData.slug);

        if (existsIndex < 0) {
          //add roomUser
          const newRoomUser = createNewRoomUser(userUpdateData);

          if (userUpdateData.slug !== manager.user.slug) {
            const roomUserEntity = createAndAddRoomUserEntity(
              manager,
              newRoomUser.userSlug,
              newRoomUser.firstName,
              room.showCursor,
            );
            newRoomUser.roomUserEntity = roomUserEntity;

            if (room.canvas) {
              room.canvas.attachChild(roomUserEntity, room.canvas);
            } else {
              setParent(roomUserEntity, room);
            }
          }

          room.users.push(newRoomUser);
        } else {
          const existingUser = room.users[existsIndex];
          existingUser.firstName = userUpdateData.firstname;
          existingUser.lastName = userUpdateData.lastname;
          existingUser.profileUriThumb = userUpdateData.profileUriThumb;
          existingUser.disconnected = userUpdateData.disconnected;
          existingUser.disconnected = userUpdateData.disconnected;
        }
      }
    }

    //remove roomUsers who are not there anymore
    for (let i = room.users.length - 1; i >= 0; i--) {
      const roomUser = room.users[i];
      const exists = userInfo.some((it) => it.slug === roomUser.slug);

      if (!exists) {
        //remove roomUser

        if (roomUser.roomUserEntity) {
          setVisibilityState(roomUser.roomUserEntity, VisibilityState.DISAPPEARING);
        }
        removeDenseArrayIndex(room.users, i);
      }
    }
  }

  //we do not want this icon anymore
  // updateRoomUserIcons(room.users, userCount, manager.overlay);
}

export function requestCloseRoom(room: Room) {
  if (!room) return;

  const roomId = room.roomId;

  const doShowLeaveWarning =
    room.navigationType === RoomNavigationType.LEAVE_MISSION ||
    room.navigationType === RoomNavigationType.LEAVE_FINISHED_MISSION;

  if (doShowLeaveWarning) {
    const roomUsers = room.users.length;

    let message = '';

    if (room.navigationType === RoomNavigationType.LEAVE_MISSION) {
      if (roomUsers === 1) {
        message = t('room.leave_desc_solo');
      } else if (roomUsers === 2) {
        const others = getOtherRoomUsersString(room, room.manager.user.slug);
        message = t('room.leave_desc_pair', { others });
      } else {
        message = t('room.leave_desc_group');
      }
    } else {
      message = t('room.leave_desc_finished');
    }
  } else {
    leaveRoom(roomId);
  }
}

// Room navigation was server-driven (the server decided which room to load next).
// In the client-only engine there is no navigation server, so these are no-ops for now.
export function leaveRoom(_roomId: RoomId) {}

export function setPrepReady(_roomId: RoomId) {}

export function requestBackToHome(_roomId: RoomId) {}

export function terminateRoom(room: Room) {
  removeGameObjectByEntity(room);
}

function createNewRoomUser(roomUserInfo: RoomUserInfo): RoomUser {
  const newRoomUser: RoomUser = {
    firstName: roomUserInfo.firstname,
    lastName: roomUserInfo.lastname,
    isHost: roomUserInfo.isHost,
    slug: roomUserInfo.slug || '',
    profileUriThumb: convertUrl(roomUserInfo.profileUriThumb),
    profileUriMedium: convertUrl(roomUserInfo.profileUriMedium),

    roomUserIndex: roomUserInfo.roomUserIndex,

    userSlug: roomUserInfo.slug,

    isReserved: roomUserInfo.isReservedUser,

    disconnected: roomUserInfo.disconnected,

    roomUserEntity: null,
  };

  return newRoomUser;
}

function createAndAddRoomUserEntity(
  manager: GameManager,
  userSlug: string,
  name: string,
  showCursor: boolean,
): RoomUserEntity {
  let mousePos = v2();

  let newOtherUserEnt: RoomUserEntity = {
    ...getZeroStaticEntity(),

    objectType: SharedObjectType.OTHER_CLIENT,

    userSlug,
    pos: mousePos,
    targetPos: mousePos,
    lastClickedTime: 0,
    cursorText: '',
    cursorTextChanged: false,
    cursorTextObject: null,
    showCursor: false,
    name,
  };

  if (showCursor) {
    setVisibilityState(newOtherUserEnt, VisibilityState.APPEARING);
  }

  addGameObject(manager, newOtherUserEnt);

  return newOtherUserEnt;
}

function update(room: Room, parentSizeChanged: boolean): boolean {
  if (parentSizeChanged) {
    if (room.canvas) {
      updateRoomCanvas(room.canvas);
    }
  }

  //Light movement when mouse moves.
  //Is really cool effect, but a lot of tiny movements on the canvas
  //creates ugly artifacts:
  //like video images swich between hdr/non hdr & text sub-pixel artifacts.
  if (false && debugState.isDebugMode && room.canvas) {
    const x = (room.relativeMousePos.x - room.width / 2) / -150;
    const y = (room.relativeMousePos.y - room.height / 2) / -150;
    room.canvas.centerAnchor.pos.x = lerpNumber(room.canvas.centerAnchor.pos.x, x, 0.1);
    room.canvas.centerAnchor.pos.y = lerpNumber(room.canvas.centerAnchor.pos.y, y, 0.1);
  }

  return false;
}

function updateRoomName(room: Room, roomName: string) {
  // const roomName = roomName;

  room.name = roomName;

  const isHostTitle = room.userIsHost ? '(host)' : '';

  document.title = room.manager.pageTitle + ' - ' + room.name;
}

function dispose(room: Room) {
  // if (room.manager.cursor.tooltip === 'home') {
  //   room.manager.cursor.requestHideTooltip = 'home';
  // }
}

export function updateRoomCanvas(canvas: Canvas) {
  if (canvas.doKill) return;

  const manager = canvas.manager;
  let room = findRoomById(manager, canvas.parentRoomId);

  let size: Size = { width: manager.space.width, height: manager.space.height };

  const TOP_MARGIN = 0;

  canvas.padWidth = 0;
  setVisibilityState(canvas, VisibilityState.DEFAULT);

  {
    canvas.padHeight = -TOP_MARGIN;
  }
  size.height = manager.space.height + canvas.padHeight;

  Background.setPadding(canvas.padWidth, canvas.padHeight);

  manager.DOM.videoContainer.style.width = size.width + 'px';
  manager.DOM.videoContainer.style.height = size.height + 'px';
}

export function getRoomUserName(room: Room, userSlug: string): string {
  if (!room) return '';

  const roomUser = room.users.find((it) => it.slug === userSlug);

  if (roomUser) return roomUser.firstName;

  return `client[${userSlug}]`;
}

function setJoined(room: Room, joined: boolean) {
  room.joined = joined;
}

export function createFriendlyRoomUserList(roomUsers: RoomUser[], max: number = 0): string {
  let result = '';

  if (max >= roomUsers.length) {
    max = roomUsers.length;
  }

  const extra = roomUsers.length - max;

  for (let i = 0; i < max; i++) {
    const user = roomUsers[i];

    if (i === 0) {
      result = `${user.firstName} ${user.lastName}`;
    } else if (i > 0 && i < max - 1) {
      result += `, ${user.firstName} ${user.lastName}`;
    } else if (extra === 0) {
      result += ` and ${user.firstName} ${user.lastName}`;
    } else {
      result += `, ${user.firstName} ${user.lastName} and ${extra} more`;
    }
  }

  return result;
}

export function createPackage(room: Room, position: V2, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (false) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      positionX: position.x,
      positionY: position.y,
      scale,
      width: room.width,
      height: room.height,
      shapeStyle: SHAPE_STYLES.DEBUG_GREEN,
      cornerRadius1: 20,
      cornerRadius2: 20,
      cornerRadius3: 20,
      cornerRadius4: 20,
      shadow: null,
    };
    rPackage.push(rect);
  }

  return rPackage;
}
